import React, { FC, FormEvent, useState } from "react";
import { ApolloProvider, gql, useMutation } from "@apollo/client";
import client from "../../../../../apollo-client";
import { useWebTranslation } from "../../../../translations";
import { getOrg } from "../../../../utils/site";
import { Input, Typography } from "@/ui-kit";
import {
  SubmitWithdrawalRequest,
  SubmitWithdrawalRequestVariables,
} from "../../../../../__generated__/graphql";
import styles from "./WithdrawalForm.module.css";

const SUBMIT_WITHDRAWAL_REQUEST = gql`
  mutation SubmitWithdrawalRequest($input: SubmitWithdrawalRequestInput!) {
    submitWithdrawalRequest(input: $input) {
      success
    }
  }
`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POLICY_NUMBER_TOKEN_REGEX =
  /^(MJAU|VOFF|BELL|WOOF|MEOW|WUFF|MIAU|MIAOU|OUAF)[-./_ A-Z0-9]{1,18}$/i;

// Customers entitled to withdrawal may not know their policy number and just
// write the product name "Lassie Plus". Accept its common variants alongside
// real policy numbers. Normalise (lowercase, strip "+", collapse whitespace)
// before matching. Mirrors the backend zod schema in lassie-shared.
const LASSIE_PLUS_VARIANTS = new Set(["lassie", "lassieplus", "lassie plus"]);

const isLassiePlusVariant = (token: string): boolean =>
  LASSIE_PLUS_VARIANTS.has(
    token.toLowerCase().replace(/\+/g, "").replace(/\s+/g, " ").trim()
  );

const isPolicyToken = (token: string): boolean =>
  POLICY_NUMBER_TOKEN_REGEX.test(token) || isLassiePlusVariant(token);

const isPolicyNumbersValid = (value: string): boolean =>
  value
    .split(/[,;\n\r]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .every(isPolicyToken);

type FormState = {
  customerName: string;
  customerEmail: string;
  customerSsn: string;
  policyNumbers: string;
};

export const WithdrawalForm: FC = () => (
  <ApolloProvider client={client}>
    <WithdrawalFormInner />
  </ApolloProvider>
);

const WithdrawalFormInner: FC = () => {
  const { t } = useWebTranslation();
  const showSsn = getOrg() === "swe";

  const [form, setForm] = useState<FormState>({
    customerName: "",
    customerEmail: "",
    customerSsn: "",
    policyNumbers: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [submitMutation, { loading }] = useMutation<
    SubmitWithdrawalRequest,
    SubmitWithdrawalRequestVariables
  >(SUBMIT_WITHDRAWAL_REQUEST);

  const update =
    (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((s) => ({ ...s, [key]: e.target.value }));

  const validate = (): string | null => {
    if (!form.customerName.trim()) return t("withdrawal.error.nameRequired");
    if (!EMAIL_REGEX.test(form.customerEmail.trim()))
      return t("withdrawal.error.emailInvalid");
    if (showSsn && !form.customerSsn.trim())
      return t("withdrawal.error.ssnRequired");
    if (!form.policyNumbers.trim())
      return t("withdrawal.error.policyNumbersRequired");
    if (!isPolicyNumbersValid(form.policyNumbers))
      return t("withdrawal.error.policyNumbersInvalid");
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    try {
      const { data } = await submitMutation({
        variables: {
          input: {
            customerName: form.customerName.trim(),
            customerEmail: form.customerEmail.trim(),
            customerSsn: showSsn ? form.customerSsn.trim() : null,
            policyNumbers: form.policyNumbers.trim(),
          },
        },
      });
      if (data?.submitWithdrawalRequest?.success) {
        setSubmitted(true);
      } else {
        setError(t("withdrawal.error.submitFailed"));
      }
    } catch {
      setError(t("withdrawal.error.submitFailed"));
    }
  };

  if (submitted) {
    return (
      <div className={styles.success}>
        <Typography variant="headingSm">
          {t("withdrawal.success.title")}
        </Typography>
        <Typography variant="bodyRg">
          {t("withdrawal.success.body")}
        </Typography>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="customerName">
          {t("withdrawal.field.customerName")}
        </label>
        <Input
          id="customerName"
          name="customerName"
          autoComplete="name"
          required
          maxLength={200}
          value={form.customerName}
          onChange={update("customerName")}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="customerEmail">
          {t("withdrawal.field.customerEmail")}
        </label>
        <Input
          id="customerEmail"
          name="customerEmail"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          value={form.customerEmail}
          onChange={update("customerEmail")}
        />
      </div>

      {showSsn && (
        <div className={styles.field}>
          <label className={styles.label} htmlFor="customerSsn">
            {t("withdrawal.field.customerSsn")}
          </label>
          <Input
            id="customerSsn"
            name="customerSsn"
            inputMode="numeric"
            required
            maxLength={20}
            value={form.customerSsn}
            onChange={update("customerSsn")}
          />
        </div>
      )}

      <div className={styles.field}>
        <label className={styles.label} htmlFor="policyNumbers">
          {t("withdrawal.field.policyNumbers")}
        </label>
        <Input
          id="policyNumbers"
          name="policyNumbers"
          required
          maxLength={500}
          value={form.policyNumbers}
          onChange={update("policyNumbers")}
        />
      </div>

      {error && (
        <div className={styles.failure}>
          <Typography variant="bodyRg" className={styles.error}>
            {error}
          </Typography>
        </div>
      )}

      <button
        type="submit"
        className={styles.submitButton}
        disabled={loading}
      >
        {loading ? t("withdrawal.submitting") : t("withdrawal.submit")}
      </button>
    </form>
  );
};
